import React from "react";
import { money, applyStandardDiscount, refinishOverheadOf } from "./doorMarginFormat";

/**
 * Net-per-door over time — one point per snapshot day (the list is already
 * latest-per-localDate). Two lines: the SELECTED refinisher's refinish Recipe vs
 * Actual (no blended view since 2026-09-27), plus maintenance Actual as a thin line. Inline SVG like AnnualRevenueChart (no
 * chart lib; CI is warnings-as-errors). Colours reuse the validated
 * BusinessMetrics set: green = actual, blue = recipe, grey = maintenance.
 */
const COLORS = { actual: "#05F842", recipe: "#2196F3", maint: "#9E9E9E" };
// The SVG is drawn 720 wide and scaled to the panel, so on a wide screen every
// label grows ~2.5x — at most this many date labels, evenly spaced, last always shown.
const MAX_X_LABELS = 8;
const shortDate = (localDate) => {
  const [, m, d] = String(localDate || "").split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : "";
};

const DoorMarginTrend = ({ history, discountPct = 0, techId = null, techName = null }) => {
  const points = [...(history || [])]
    .sort((a, b) => Number(a.computedAt) - Number(b.computedAt))
    .map((h) => ({ h, t: (h.perRefinisher || []).find((p) => p.userId === techId) || null }))
    .map(({ h, t }) => ({
      label: shortDate(h.localDate),
      // No recipe saved yet -> no door price -> the "net" would just be minus the
      // overhead. Leave a gap instead of plotting a fake loss.
      // Same standard-discount lens as the Refinish card, so the chart and the card agree.
      recipe:
        t?.refinish?.recipe?.doorPriceCents == null
          ? null
          : applyStandardDiscount(t.refinish.recipe, {
              discountPct,
              payPct: t.payPct,
              overheadCents: refinishOverheadOf(h.overheadPerDoor),
            })?.netCents,
      actual: t?.refinish?.actual?.netCents ?? null,
      maint: h.maintenance?.actual?.netCents,
    }));

  if (points.length < 2) {
    return (
      <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
        <h3 className="bm-panel-title">Trend</h3>
        <div className="bm-empty-small">A trend needs at least two snapshot days — the Wednesday worker adds one a week.</div>
      </div>
    );
  }

  const W = 720;
  const H = 220;
  const padL = 64;
  const padR = 16;
  const padT = 16;
  const padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const vals = points.flatMap((p) => [p.recipe, p.actual, p.maint]).filter((v) => v != null).map(Number);
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const x = (i) => padL + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v) => padT + plotH - ((Number(v) - min) / (max - min || 1)) * plotH;
  const path = (key) =>
    points
      .map((p, i) => (p[key] == null ? null : `${i === 0 || points[i - 1][key] == null ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`))
      .filter(Boolean)
      .join(" ");
  const ticks = 4;
  const labelStep = Math.max(1, Math.ceil(points.length / MAX_X_LABELS));
  const showLabel = (i) => i === points.length - 1 || (i % labelStep === 0 && points.length - 1 - i >= labelStep / 2);

  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <h3 className="bm-panel-title">
        Trend — net per door ({techName || "refinish"}{discountPct > 0 ? `, recipe less ${discountPct}%` : ""})
      </h3>
      <div className="bm-legend">
        <span className="bm-legend-item"><span className="bm-legend-dot" style={{ background: COLORS.actual }} /> Actual</span>
        <span className="bm-legend-item"><span className="bm-legend-dot" style={{ background: COLORS.recipe }} /> Recipe</span>
        <span className="bm-legend-item"><span className="bm-legend-dot" style={{ background: COLORS.maint }} /> Maintenance (actual)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="bm-svg" role="img" aria-label="Net per door over time">
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const v = min + ((max - min) * i) / ticks;
          const yy = y(v);
          return (
            <g key={i}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} className="bm-grid" />
              <text x={padL - 8} y={yy + 4} textAnchor="end" className="bm-axis-text">
                {money(v)}
              </text>
            </g>
          );
        })}
        {min < 0 && <line x1={padL} y1={y(0)} x2={W - padR} y2={y(0)} className="bm-grid-strong" />}
        <path d={path("maint")} fill="none" stroke={COLORS.maint} strokeWidth="1.5" strokeDasharray="4 3" />
        <path d={path("recipe")} fill="none" stroke={COLORS.recipe} strokeWidth="2" />
        <path d={path("actual")} fill="none" stroke={COLORS.actual} strokeWidth="2.5" />
        {points.map((p, i) => (
          <g key={i}>
            {p.actual != null && <circle cx={x(i)} cy={y(p.actual)} r="3" fill={COLORS.actual} />}
            {p.recipe != null && <circle cx={x(i)} cy={y(p.recipe)} r="2.5" fill={COLORS.recipe} />}
            {showLabel(i) && (
              <text x={x(i)} y={H - padB + 16} textAnchor="middle" className="bm-axis-text">
                {p.label}
              </text>
            )}
          </g>
        ))}
        <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} className="bm-axis-line" />
      </svg>
    </div>
  );
};

export default DoorMarginTrend;
